// src/redux/store.ts

import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { persistReducer, persistStore } from 'redux-persist';
import storage from 'redux-persist/lib/storage'; // ✅ Correct import

// Import your reducers
import talentFormReducer from './slices/talentFormSlice';
import authReducer from './slices/authSlice';
// import dashboardReducer from './slices/dashboardSlice';

// --- START: Safe Storage Utility ---
// This handles cases where localStorage might not be available
// (e.g., Safari private mode, server-side rendering)

const createNoopStorage = () => {
  return {
    getItem(_key: string) {
      return Promise.resolve(null);
    },
    setItem(_key: string, _value: any) {
      return Promise.resolve();
    },
    removeItem(_key: string) {
      return Promise.resolve();
    },
  };
};

const createSafeStorage = () => {
  try {
    // Check if we're in a browser environment
    if (typeof window !== 'undefined') {
      // Test if localStorage is actually available
      const test = 'redux-persist-test';
      window.localStorage.setItem(test, test);
      window.localStorage.removeItem(test);
      return storage; // ✅ Return the imported storage
    }
  } catch (error) {
    console.warn('localStorage not available, using noop storage:', error);
  }
  // Fallback to noop storage if localStorage isn't available
  return createNoopStorage();
};

const safeStorage = createSafeStorage();

// --- END: Safe Storage Utility ---

// Combine all your reducers into one root reducer
const rootReducer = combineReducers({
  talentForm: talentFormReducer,
  auth: authReducer,
  // dashboard: dashboardReducer,
  // Add more reducers here as your app grows
});

// Configuration for redux-persist
const persistConfig = {
  key: 'root', // Key for localStorage (will be 'persist:root')
  version: 1, // Version number for migrations
  storage: safeStorage, // Use our safe storage
  // Optional: Only persist specific reducers
  // whitelist: ['talentForm', 'auth'],
  // Optional: Exclude specific reducers from persistence
  // blacklist: ['someTemporaryData'],
};

// Wrap the root reducer with persistReducer
const persistedReducer = persistReducer(persistConfig, rootReducer);

// Create the Redux store
export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // Ignore these action types from redux-persist
        ignoredActions: [
          'persist/PERSIST',
          'persist/REHYDRATE',
          'persist/REGISTER',
        ],
        // If you're storing File objects or other non-serializable data:
        // ignoredPaths: ['talentForm.basicInfo.professionalPhoto'],
      },
    }),
  // Enable Redux DevTools in development
  devTools: process.env.NODE_ENV !== 'production',
});

// Create the persistor
export const persistor = persistStore(store);

// ✅ Automatically infer the RootState type from the store
export type RootState = ReturnType<typeof store.getState>;

// ✅ Infer the AppDispatch type for thunks and async actions
export type AppDispatch = typeof store.dispatch;
